import PropTypes from "prop-types";
import React from "react";
import { formatHexStr } from "../utils/formatHexStr";
import {
  ActionIcon,
  Alert,
  Box,
  CopyButton,
  Flex,
  Image,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import {
  IconAlertSquareRounded,
  IconCheck,
  IconCopy,
} from "@tabler/icons-react";
import { useStylesStampResult } from "../utils/mantine/useStylesStampResult";
import { useStylesPage } from "../utils/mantine/useStylesPage";
import CustomButton from "./mantineCustomComponents/CustomButton";
import { BorderedContent } from "./BorderedContent";
import {
  HASH_ALGORITHM_SHA3_256,
  HASH_ALGORITHM_KECCAK_256,
} from "../utils/cryptoUtils";
import { useStylesTechnicalDetail } from "../utils/mantine/useStylesTechnicalDetail";
import { useStylesIcons } from "../utils/mantine/useStylesIcons";

const StampNotFound = ({
  hash,
  staticBasePath,
  handleDefaultScreen,
  verificationMethod,
}) => {
  //css classes
  const { classes: classesStampResult } = useStylesStampResult();
  const { classes: classesPage } = useStylesPage();
  const { classes: classesTechnicalDetail } = useStylesTechnicalDetail();
  const { classes: classesIcons } = useStylesIcons();

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  return (
    <Box
      className={`${classesStampResult.stamp} ${classesPage.page} stamp-result`}
    >
      <Image
        src={`${staticBasePath}public/images/svg/vb-icon-not-verified.svg`}
        alt="Red checkmark"
        className={classesPage.pageHeaderIcon}
      />

      <Title order={1}>No Stamp Found!</Title>

      <BorderedContent additionalClassName="borders-no">
        <Box className="details">
          <Box className={classesTechnicalDetail.technical}>
            <Alert
              variant="light"
              className={classesTechnicalDetail.alert}
              color="black"
              title="Stamps created in the last 10 seconds may not be visible yet."
              icon={<IconAlertSquareRounded />}
            ></Alert>

            <Flex gap={theme.flex.gap.xs}>
              <Text className="title">Technical Details</Text>
            </Flex>
            <Flex
              gap={theme.flex.gap.xxs}
              justify={theme.flex.justify.between}
              className="item"
              align={theme.flex.align.end}
            >
              <Text className="key">Content ID</Text>
              <Box className="dots"></Box>
              <Text className="value">
                {formatHexStr(hash)}

                <CopyButton value={hash} timeout={2000}>
                  {({ copied, copy }) => (
                    <Tooltip
                      label={copied ? "Copied" : "Copy"}
                      className={classesIcons.copy}
                      withArrow
                      position="right"
                    >
                      <ActionIcon
                        color={
                          copied
                            ? theme.customColors.actionIconFirst
                            : theme.customColors.actionIconSecond
                        }
                        variant="subtle"
                        onClick={copy}
                      >
                        {copied ? (
                          <IconCheck className={classesIcons.width} />
                        ) : (
                          <IconCopy className={classesIcons.width} />
                        )}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Text>
            </Flex>
            {verificationMethod !== "hash" && (
              <Box pt={theme.padding.lgEm} className="item">
                <Text className="key">
                  ContentID verified using {HASH_ALGORITHM_SHA3_256} and{" "}
                  {HASH_ALGORITHM_KECCAK_256} algorithms.
                </Text>
              </Box>
            )}
          </Box>
        </Box>
      </BorderedContent>
      <Flex
        align={theme.flex.align.center}
        justify={theme.flex.justify.center}
        mt={theme.margin.xxlEm}
        gap={theme.flex.gap.xs}
      >
        <CustomButton
          text="Verify Another Stamp"
          onClick={handleDefaultScreen}
          componentClasses="buttonBlack buttonBlack-verify anchor"
        ></CustomButton>
      </Flex>
    </Box>
  );
};

StampNotFound.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
  hash: PropTypes.string.isRequired,
  verificationMethod: PropTypes.string.isRequired,
  handleDefaultScreen: PropTypes.func,
};

export default StampNotFound;
